import Underline from '@tiptap/extension-underline';
import { Focus, Placeholder } from '@tiptap/extensions';
import { useEditor } from '@tiptap/react';
import { debounce } from 'lodash';
import { useEffect, useRef } from 'react';
import BlockHandle from '../LessonPlan/ColumnBlockHandle';
import {
  EditorContainer,
  EditorWrapper,
  StyledEditorContent,
} from '../LessonPlan/editorStyles';
import FixedToolbar from '../LessonPlan/FixedToolbar';
import { sharedEditorExtensions } from '../TipTapCustomExtensions/sharedEditorExtensions';

interface LearnerNoteEditorProps {
  content: string;
  onContentChange: (content: string) => void;
  editable?: boolean;
}

export default function LearnerNoteEditor({
  content,
  onContentChange,
  editable = true,
}: LearnerNoteEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: [
      ...sharedEditorExtensions,
      Underline,
      Focus.configure({
        className: 'has-focus',
        mode: 'all',
      }),
      Placeholder.configure({
        placeholder: 'Write your notes here...',
      }),
    ],
    content: '',
    editable,
  });

  const handlePrint = () => {
    if (!editorRef.current) return;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(iframe);
      return;
    }

    const stylesheets = Array.from(
      document.querySelectorAll('style, link[rel="stylesheet"]'),
    );
    const stylesHTML = stylesheets.map((s) => s.outerHTML).join('\n');

    doc.open();
    doc.write(`
      <html>
        <head>
          ${stylesHTML}
          <style>@page { margin: 1.5cm; }</style>
        </head>
        <body>${editorRef.current.outerHTML}</body>
      </html>
    `);
    doc.close();

    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();

    setTimeout(() => document.body.removeChild(iframe), 1000);
  };

  // Sync content from prop to editor
  useEffect(() => {
    if (editor && content) {
      if (editor.getHTML() !== content) {
        editor.commands.setContent(content);
      }
    }
  }, [editor, content]);

  // Sync content from editor to parent
  useEffect(() => {
    const debouncedUpdate = debounce((newContent: string) => {
      onContentChange(newContent);
    }, 300);

    if (editor) {
      const handleContentUpdate = () => {
        const newContent = editor.getHTML();
        debouncedUpdate(newContent);
      };

      editor.on('update', handleContentUpdate);
      editor.on('transaction', ({ transaction }) => {
        if (transaction.docChanged) {
          handleContentUpdate();
        }
      });

      return () => {
        editor.off('update', handleContentUpdate);
        editor.off('transaction');
        debouncedUpdate.cancel();
      };
    }
  }, [editor, onContentChange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) {
    return null;
  }

  return (
    <EditorContainer>
      {editable && <FixedToolbar editor={editor} onExportToPdf={handlePrint} />}
      {editable && <BlockHandle editor={editor} />}
      <EditorWrapper className="EditorWrapper">
        <StyledEditorContent editor={editor} ref={editorRef} />
      </EditorWrapper>
    </EditorContainer>
  );
}
